import Tile from '../common/Tile';
import { instrumentTypeBadge, shortenSourceName } from '../../utils/helpers';
import { useState, useRef, useEffect, useCallback } from 'react';
import { getAuthToken, setDjangoUnchainedTOS, setTrendExtension } from '../../services/api';
import { timeframesTos } from '../../utils/helpers';
import ConfigPanelForColors from './ConfigPanelForColors';
import { setActiveTimeframe as setActiveTimeframeAPI } from '../../services/api';
import WebSocketStatusBadge from './WebSocketStatusBadge';
import InfluenceArrowsEditor from './InfluenceArrowsEditor';
import TimeframeMatrix from './TimeframeMatrix';
import { trendExtensionView } from './TrendExtensionBadge';
import useHitFavorites from '../Notifications/useHitFavorites';
import RangeFinderPanel from './RangeFinderPanel';
import { isRangeFinderFeatureEnabled } from '../../config/featureFlags';
import {
  getCurrentSeniorDuLevel,
  waveMatchesParityMode,
  normalizeWavePairsForParity,
  getDuWaveButtonStateClass,
  parseQuantityValue,
  createInitialDuVisibility,
  enforceExpandedDuSelector,
} from "./tosPanelDuHelpers";

const DU_PARITY_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'odd', label: 'Odd' },
];

const DU_WAVE_KEYS = ['u1', 'u2', 'u3', 'u4', 'u5', 'd1', 'd2', 'd3', 'd4', 'd5'];
const DU_LEVEL_ORDER = ['tactical', 'optimal', 'strategic'];
export const TOS_PANEL_SOLO_GRID_LOCATION = 'sm:col-start-2 sm:col-end-4 sm:row-start-1 sm:row-end-1 xl:col-start-2 xl:col-end-3 col-start-1 col-end-1 row-start-2 row-end-2';
const EMPTY_WAVE_PAIRS = {
  u1: false, d1: false,
  u2: false, d2: false,
  u3: false, d3: false,
  u4: false, d4: false,
  u5: false, d5: false
};

// Covers the packet that left the server before our save landed and arrives
// right after it: anything older than this is stale by definition.
const DU_SOCKET_MUTE_MS = 2000;

// Mirrors config.SCANNER_WAVE_FILTERS on the backend (same order, cycled by one button).
// TRADING mode: 'off' (default) plus the same labels.
const SCANNER_WAVE_FILTER_LABELS = {
  first: 'WAVE 1',
  first_fifth: 'WAVES 1+5',
  all: 'ALL WAVES',
  off: 'WAVES OFF', // TRADING only
};

// The one-letter badges left of the symbol. A whitelist, so a status the panel
// does not know about is skipped instead of drawing an empty badge — the socket
// carries plenty of keys that are data, not flags.
const STATUS_BADGES = {
  is_otros_based: () => 'OTROS',
  tros_stage: (value) => `S${value}`,
  is_j2f: () => 'J',
  is_spin_down: () => 'S',
  is_optimal_timeframe: () => 'OT',
  is_defense_block: () => 'DB',
  is_synthetic: () => 'SYNT',
  // The odd one out: it marks the algo being OFF.
  is_algo_active: (value) => (value === false ? 'F' : null),
};

// null = no badge for this status.
export const statusBadgeLabel = (key, value) => {
  const label = STATUS_BADGES[key];
  if (!label) return null;
  if (key !== 'is_algo_active' && !value) return null;
  return label(value) || null;
};

const createEmptyWavePairs = () => ({ ...EMPTY_WAVE_PAIRS });
const createEmptyDuLevelConfig = () => ({
  parityMode: 'all',
  closeMode: false,
  wavePairs: createEmptyWavePairs(),
  persistentWavePairs: createEmptyWavePairs(),
});
const createEmptyDuConfigs = () => ({
  tactical: createEmptyDuLevelConfig(),
  optimal: createEmptyDuLevelConfig(),
  strategic: createEmptyDuLevelConfig(),
});
const hasSelectedDuWaves = (levelConfig = {}) => (
  Object.values(levelConfig.wavePairs || {}).some(Boolean) ||
  Object.values(levelConfig.persistentWavePairs || {}).some(Boolean)
);

const extractLevelConfig = (levelConfig = {}) => ({
  parityMode: levelConfig.parity_mode || 'all',
  closeMode: levelConfig.close_mode || false,
  wavePairs: {
    u1: levelConfig.u1 || false,
    d1: levelConfig.d1 || false,
    u2: levelConfig.u2 || false,
    d2: levelConfig.d2 || false,
    u3: levelConfig.u3 || false,
    d3: levelConfig.d3 || false,
    u4: levelConfig.u4 || false,
    d4: levelConfig.d4 || false,
    u5: levelConfig.u5 || false,
    d5: levelConfig.d5 || false,
  },
  persistentWavePairs: {
    u1: levelConfig.pu1 || false,
    d1: levelConfig.pd1 || false,
    u2: levelConfig.pu2 || false,
    d2: levelConfig.pd2 || false,
    u3: levelConfig.pu3 || false,
    d3: levelConfig.pd3 || false,
    u4: levelConfig.pu4 || false,
    d4: levelConfig.pd4 || false,
    u5: levelConfig.pu5 || false,
    d5: levelConfig.pd5 || false,
  },
});
const TOSPanel = ({
  asset,
  handleToggleSide,
  handleChangeActivePos,
  gridLocation,
  timeframe,
  username,
  isVirtualAccount,
  leverageInitial,
  strategy = 'tos',
  isSenior = false,
  synchronizeChangesToLinkedAccounts = () => { },
  onTimeframeChange = () => { },
  handleSetTimeframeSideClick = () => { },
  onStsDataChange = () => { },
  onQuantitySet = () => { },
  onTOSPatch = () => { },
  handleCounterStateChange = () => { },
  handleSetLeverage = () => { },
  noBottomMargin = false,
  // In a folder the instrument is one thing, not one per account — no number.
  showAccount = true,
  onDjangoUnchainedChange = () => { },
  onSourcesChange = () => { },
  onPatchChange = () => { },
  onReconnect = () => { },
  // COUNTER opts out of these; default true keeps tos-solo unchanged.
  showDuWaves = true,
  showSideSignals = true,
  showQty = true,
  // TIMEFRAMES selector starts folded (Dashboard Favorites' in-card scanner panel).
  timeframesCollapsed = false,
  // TRADING / SCANNER switch, rendered next to the account name. Only the views
  // that pass a handler get the button; tos-solo keeps its own in ConfigPanel.
  tradingInstrumentActive = false,
  onToggleTradingInstrument = null,
  scannerWaveFilter = 'first',
  onCycleScannerWaveFilter = null,
}) => {
  const ws = useRef();

  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([])
  const [gyroxArray, setGyroxArray] = useState([]);
  const [isActiveMenuOpen, setIsActiveMenuOpen] = useState(false);
  const [wsConnected, setwsConnected] = useState(false);
  const [stsData, setStsData] = useState(null)
  const [stsActive, setStsActive] = useState(stsData?.updown_target || stsData?.fade_target || stsData?.fade_shabbat || stsData?.trading_spagat || stsData?.jump_to || stsData?.shabbat);
  const [quantitiesInput, setQuantitiesInput] = useState({});
  const [countersData, setCountersData] = useState({});
  const [timeframeStates, setTimeframeStates] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [leverage, setLeverage] = useState();
  const [duLevelConfigs, setDuLevelConfigs] = useState(createEmptyDuConfigs);
  const [showDuSelectors, setShowDuSelectors] = useState(createInitialDuVisibility);
  const [activeWave, setActiveWave] = useState(null);
  const [influenceArrows, setInfluenceArrows] = useState({});
  const [trendExtension, setTrendExtensionState] = useState({});
  // Static ×4 pairing {junior: senior}, served with the socket statuses so the
  // panel never keeps its own copy of the rule (db_client/.../timeframe_ladder.py).
  const [x4Ladder, setX4Ladder] = useState({});
  const isInitialDataLoaded = useRef(false);
  // The optimistic flip below would be undone by a socket packet built from Redis
  // before our save landed. Ignore DU config from the socket while a save is in
  // flight, and briefly after it, to let that stale packet pass by.
  const duSavesInFlight = useRef(0);
  const duSocketMuteUntil = useRef(0);
  // Same account key as the Live feed, so a star here lands in its Favorites channel.
  const { favs, toggleFav } = useHitFavorites(asset.broker === 'okx' ? username : asset.account, asset.broker, asset.symbol);
  const [showUPanel, setShowUPanel] = useState(true);
  const [showLPanel, setShowLPanel] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectTimerRef = useRef(null);
  const currentSeniorDuLevel = getCurrentSeniorDuLevel(quantitiesInput);
  const quantityTimersRef = useRef({});
  const dropdownRefs = useRef({});
  // Keys match backend model: u1_quantity, l1_quantity (no underscore between letter and number)
  const sourceToLevelMap = {
    't': 'tactical',
    'm': 'optimal',
    's': 'strategic',
    'l1': 'l1',
    'l2': 'l2',
    'l3': 'l3',
    'u1': 'u1',
    'u2': 'u2',
    'u3': 'u3'
  }
  const counterOptions = ["--", "U1", "U2", "U3", "U4", "U5", "D1", "D2", "D3", "D4", "D5"];
  const closeWebsocket = () => {
    if (ws.current) {
      const currentSocket = ws.current;
      currentSocket.onclose = null;
      currentSocket.onerror = null;
      ws.current = null;
      currentSocket.close();
    }
    setwsConnected(false);
  };
  const rangeFinderEnabled = isRangeFinderFeatureEnabled();

  useEffect(
    () => {
      function handleClickOutside(event) {
        let clickedInsideAnyDropdown = false;

        // Check if clicked inside any of our dropdowns
        Object.values(dropdownRefs.current).forEach(ref => {
          if (ref && ref.contains(event.target)) {
            clickedInsideAnyDropdown = true;
          }
        });

        if (!clickedInsideAnyDropdown) {
          setOpenDropdown(null);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      if (!wsConnected) {
        connectWebsocket();
      }
      return () => {
        closeWebsocket();
        Object.values(quantityTimersRef.current || {}).forEach((timerId) => {
          if (timerId) clearTimeout(timerId);
        });
        document.removeEventListener("mousedown", handleClickOutside);
      }
    }, []
  );

  useEffect(
      () => {
        if (wsConnected) {
          isInitialDataLoaded.current = false;
          closeWebsocket();
          connectWebsocket();
        }
      }, [timeframe]
  )

  const getStrengthLevel = (counterState) => {
    if (!counterState) return null;
    if (counterState === "D4" || counterState === "U4") return "LOW";
    if (counterState === "D5" || counterState === "U5") return "MEDIUM";
    if (counterState === "D1" || counterState === "U1") return "HIGH";
    return null;
  };

  const handleLeverageChange = (leverageLocal) => {
    setLeverage(leverageLocal);
    handleSetLeverage(asset.symbol, asset.broker, timeframe, leverageLocal);
  };

  const persistDjangoUnchainedConfig = async (nextDuLevelConfigs) => {
    const nextDuConfig = {
      tactical: nextDuLevelConfigs.tactical,
      optimal: nextDuLevelConfigs.optimal,
      strategic: nextDuLevelConfigs.strategic,
    };
    duSavesInFlight.current += 1;
    try {
      await setDjangoUnchainedTOS(
        asset.symbol,
        asset.broker,
        asset.broker === 'okx' ? username : asset.account,
        asset.sectype,
        timeframe,
        nextDuConfig
      );
    } finally {
      duSavesInFlight.current -= 1;
      duSocketMuteUntil.current = Date.now() + DU_SOCKET_MUTE_MS;
    }

    onDjangoUnchainedChange(nextDuConfig);
  };

  const toggleWavePairSelection = (wavePairs, persistentWavePairs, pairKey, mode = 'erasable', parityMode = 'all') => {
    if (!waveMatchesParityMode(pairKey, parityMode)) {
      return { wavePairs, persistentWavePairs };
    }

    const nextWavePairsDraft = { ...wavePairs };
    const nextPersistentDraft = { ...persistentWavePairs };

    if (mode === 'persistent') {
      const isPersistentSelected = !!nextPersistentDraft[pairKey];
      nextWavePairsDraft[pairKey] = !isPersistentSelected;
      nextPersistentDraft[pairKey] = !isPersistentSelected;
    } else {
      const isErasableSelected = !!nextWavePairsDraft[pairKey] && !nextPersistentDraft[pairKey];
      if (isErasableSelected) {
        nextWavePairsDraft[pairKey] = false;
        nextPersistentDraft[pairKey] = false;
      } else {
        nextWavePairsDraft[pairKey] = true;
        nextPersistentDraft[pairKey] = false;
      }
    }

    const nextWavePairs = parityMode === 'all'
      ? nextWavePairsDraft
      : normalizeWavePairsForParity(nextWavePairsDraft, parityMode, DU_WAVE_KEYS);
    const nextPersistentWavePairs = parityMode === 'all'
      ? nextPersistentDraft
      : normalizeWavePairsForParity(nextPersistentDraft, parityMode, DU_WAVE_KEYS);

    Object.keys(nextPersistentWavePairs).forEach((key) => {
      if (!nextWavePairs[key]) {
        nextPersistentWavePairs[key] = false;
      }
    });

    return {
      wavePairs: nextWavePairs,
      persistentWavePairs: nextPersistentWavePairs,
    };
  };

  const handleDuWavePairToggle = async (levelKey, pairKey, mode = 'erasable') => {
    const levelConfig = duLevelConfigs[levelKey] || createEmptyDuLevelConfig();
    const parityMode = levelConfig.parityMode || 'all';
    if (!waveMatchesParityMode(pairKey, parityMode)) {
      return;
    }
    const { wavePairs: nextWavePairs, persistentWavePairs: nextPersistentWavePairs } = toggleWavePairSelection(
      levelConfig.wavePairs,
      levelConfig.persistentWavePairs,
      pairKey,
      mode,
      parityMode
    );
    const nextDuLevelConfigs = {
      ...duLevelConfigs,
      [levelKey]: {
        parityMode,
        closeMode: levelConfig.closeMode || false,
        wavePairs: nextWavePairs,
        persistentWavePairs: nextPersistentWavePairs,
      },
    };

    setDuLevelConfigs(nextDuLevelConfigs);
    await persistDjangoUnchainedConfig(nextDuLevelConfigs);
  };

  const handleDuParityModeChange = async (levelKey, nextParityMode) => {
    const levelConfig = duLevelConfigs[levelKey] || createEmptyDuLevelConfig();
    const normalizedWavePairs = normalizeWavePairsForParity(levelConfig.wavePairs, nextParityMode, DU_WAVE_KEYS);
    const normalizedPersistentWavePairs = normalizeWavePairsForParity(levelConfig.persistentWavePairs, nextParityMode, DU_WAVE_KEYS);
    const nextDuLevelConfigs = {
      ...duLevelConfigs,
      [levelKey]: {
        parityMode: nextParityMode,
        closeMode: levelConfig.closeMode || false,
        wavePairs: normalizedWavePairs,
        persistentWavePairs: normalizedPersistentWavePairs,
      },
    };
    setDuLevelConfigs(nextDuLevelConfigs);
    await persistDjangoUnchainedConfig(nextDuLevelConfigs);
  };

  const handleDuCloseModeToggle = async (levelKey) => {
    const levelConfig = duLevelConfigs[levelKey] || createEmptyDuLevelConfig();
    const nextDuLevelConfigs = {
      ...duLevelConfigs,
      [levelKey]: {
        ...levelConfig,
        closeMode: !levelConfig.closeMode,
      },
    };
    setDuLevelConfigs(nextDuLevelConfigs);
    await persistDjangoUnchainedConfig(nextDuLevelConfigs);
  };

  const getColorType = (counterState) => {
    if (!counterState) return null;
    if (["D4", "U1", "U5"].includes(counterState)) return "GREEN";
    if (["D1", "D5", "U4"].includes(counterState)) return "RED";
    return null;
  };

  const getBlockStyle = (strengthLevel, colorType) => {
    if (!strengthLevel || !colorType) {
      return "border border-gray-200 bg-gray-50";
    }
    if (colorType === "GREEN") {
      switch (strengthLevel) {
        case "LOW":
          return "border border-emerald-200 bg-emerald-50";
        case "MEDIUM":
          return "border border-emerald-300 bg-emerald-100";
        case "HIGH":
          return "border border-emerald-400 bg-emerald-100";
        default:
          return "border border-gray-200 bg-gray-50";
      }
    } else if (colorType === "RED") {
      switch (strengthLevel) {
        case "LOW":
          return "border border-red-200 bg-red-50";
        case "MEDIUM":
          return "border border-red-300 bg-red-100";
        case "HIGH":
          return "border border-red-400 bg-red-100";
        default:
          return "border border-gray-200 bg-gray-50";
      }
    }
    return "border border-gray-200 bg-gray-50";
  };


  // Set the wave of a specific display timeframe (not necessarily the active
  // one). target_timeframe tells the backend which timeframe to write.
  const handleTileWaveChange = (level, tileTimeframe, wave) => {
    setOpenDropdown(null);
    handleCounterStateChange(
      wave,
      level,
      timeframe,
      asset.symbol,
      asset.broker,
      asset.sectype,
      timeframe,
      'tos',
      asset.account,
      tileTimeframe,
    );
  };

  // Conjugacy of a tile against its ×4 partner — levels do not scope the pair, the
  // ratio does. A tile whose partner is not selected (or which has none at the top
  // of the ladder) shows nothing.
  const allSelectedTimeframes = () => new Set(
    ['tactical', 'optimal', 'strategic'].flatMap(
      (lvl) => quantitiesInput[`${lvl}_display_timeframes`] || []
    )
  );

  // Normally one senior; 2m is watched against both 8m and 6m, and a level's own
  // active timeframe is always watched against the senior level's active whatever
  // the multiplier — so a tile can carry more than one badge.
  const boundSeniorOf = (tf) => {
    if (tf && tf === quantitiesInput.tactical_timeframe) {
      return quantitiesInput.optimal_timeframe || null;
    }
    if (tf && tf === quantitiesInput.optimal_timeframe) {
      return quantitiesInput.strategic_timeframe || null;
    }
    return null;
  };

  const seniorsOf = (tf) => {
    const selected = allSelectedTimeframes();
    const seniors = (x4Ladder?.[tf] || []).filter((senior) => selected.has(senior));
    const bound = boundSeniorOf(tf);
    if (bound && bound !== tf && !seniors.includes(bound)) seniors.push(bound);
    return seniors;
  };

  const tileConjugacy = (tf, senior) => (
    // A pair with no coupling yet has no entry; fall back to a reset badge so the
    // tile always shows something visible and clickable to override.
    influenceArrows?.pairs?.[tf]?.[senior] || { status: 'reset', arrows: [], counter: 0 }
  );

  // TE runs over the same pairs as the conjugacy, so every pair editor carries it.
  const trendExtensionFor = (tf, senior) => ({
    ...trendExtensionView(trendExtension?.[tf]?.[senior]),
    onPick: (phase) => setTrendExtension({
      symbol: asset.symbol,
      broker: asset.broker,
      account: asset.broker === 'okx' ? username : asset.account,
      sectype: asset.sectype,
      group: timeframe,
      view: 'solo',
      juniorTimeframe: tf,
      seniorTimeframe: senior,
      phase,
    }),
  });

  // One tile per selected timeframe: wave + conjugacy badge (vs senior's active).
  // LMB picks the wave for that timeframe, RMB makes it the level's active one.
  const renderLevelTiles = (level) => {
    const list = quantitiesInput[`${level}_display_timeframes`] || [];
    const activeTf = quantitiesInput[`${level}_timeframe`] || null;
    if (list.length === 0) {
      return (
        <div className="flex items-center justify-center h-7 text-[10px] font-semibold text-red-500">
          no timeframes selected
        </div>
      );
    }
    return (
      <div className="flex gap-1">
        {list.map((tf) => {
          const wave = timeframeStates[tf]?.wave || '--';
          const isActive = activeTf === tf;
          const dropdownKey = `${level}-tile-${tf}`;
          const isOpen = openDropdown === dropdownKey;
          return (
            <div
              key={tf}
              ref={el => dropdownRefs.current[dropdownKey] = el}
              className={`relative flex-1 min-w-0 rounded-lg border px-1 py-0.5 transition-colors ${
                isActive ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-white'
              }`}
              onContextMenu={(e) => { e.preventDefault(); handleSetActiveTile(level, tf); }}
              title={isActive ? `${tf} — active (traded). RMB another to switch.` : `${tf}. RMB to make active.`}
            >
              <div className="flex items-center justify-between gap-0.5">
                {/* Live feed favourites: name and star together toggle its Favorites channel. */}
                {toggleFav ? (
                  <button
                    type="button"
                    onClick={() => toggleFav(tf)}
                    aria-label={favs.includes(tf) ? `Unfavourite ${tf}` : `Favourite ${tf}`}
                    title={favs.includes(tf) ? 'Remove from Live feed favourites' : 'Add to Live feed favourites'}
                    className="group flex min-w-0 items-center gap-0.5"
                  >
                    <span className={`font-mono text-[9px] ${isActive ? 'font-bold text-indigo-700' : 'text-gray-500'}`}>
                      {tf}
                    </span>
                    <span className={`text-xs leading-none transition-colors ${favs.includes(tf) ? 'text-amber-500' : 'text-gray-300 group-hover:text-amber-400'}`}>
                      {favs.includes(tf) ? '★' : '☆'}
                    </span>
                  </button>
                ) : (
                  <span className={`font-mono text-[9px] ${isActive ? 'font-bold text-indigo-700' : 'text-gray-500'}`}>
                    {tf}
                  </span>
                )}
                {/* Conjugacy vs the ×4 partner. Editable per timeframe: the badge
                    opens the override for exactly that pair. A timeframe whose
                    partner is not selected shows nothing. */}
                {seniorsOf(tf).length ? seniorsOf(tf).map((senior) => (
                  <InfluenceArrowsEditor
                    key={senior}
                    juniorTimeframe={tf}
                    seniorTimeframe={senior}
                    asset={asset}
                    username={username}
                    timeframe={timeframe}
                    state={tileConjugacy(tf, senior)}
                    trendExtension={trendExtensionFor(tf, senior)}
                  />
                )) : (
                  <span className="text-[9px] text-gray-300" title="Нет выбранного ×4 старшего таймфрейма для этой пары">—</span>
                )}
              </div>
              <button
                onClick={() => setOpenDropdown(isOpen ? null : dropdownKey)}
                className="mt-0.5 w-full rounded bg-gray-100 py-0.5 text-center text-[11px] font-mono text-gray-700 hover:bg-gray-200 transition-colors"
                title="Set wave"
              >
                {wave}
              </button>
              {isOpen && (
                <div
                  className="absolute mt-1 w-14 bg-white border border-gray-200 rounded-lg shadow-xl"
                  style={{ top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 99999 }}
                >
                  <div className="max-h-48 overflow-y-auto py-1">
                    {counterOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleTileWaveChange(level, tf, option)}
                        className={`block w-full text-left px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-100 transition-colors
                          ${wave === option ? 'bg-indigo-100 text-indigo-700 font-semibold' : ''}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleSetActiveTile = (level, tf) => {
    // RMB on a tile → make it the active (traded) timeframe for the level.
    handleSetActiveDisplayTimeframe(level, tf);
  };


  // A level trades the real timeframe it is bound to. Binding is a direct click
  // in the timeframe grid below; every level must be bound or the backend
  // refuses the signal outright.
  // The patch is per-key: send only the field we touched (quantities/binding
  // panels never wipe each other). Backend normalizes uniqueness + active-in-list.
  const patchTimeframeField = (field, value) => {
    setQuantitiesInput(prev => ({ ...prev, [field]: value }));
    onTOSPatch(
      {
        tactical: quantitiesInput.tactical,
        optimal: quantitiesInput.optimal,
        strategic: quantitiesInput.strategic,
      },
      timeframe,
      { [field]: value },
      asset,
    );
  };

  // From the matrix: replace a level's display list (up to 3).
  const handleSetDisplayList = (level, nextList) =>
    patchTimeframeField(`${level}_display_timeframes`, nextList);

  // RMB on a tile: make that timeframe the level's active (traded) one.
  const handleSetActiveDisplayTimeframe = (level, timeframe) =>
    patchTimeframeField(`${level}_timeframe`, timeframe);

  const handleSetActiveTimeframeAPI = async (timeframe) => {
    if (isVirtualAccount) {
      return synchronizeChangesToLinkedAccounts(
        setActiveTimeframeAPI,
        asset.symbol,
        asset.broker,
        timeframe,
        'tos',
        asset.broker === 'okx' ? username : asset.account,
        asset.sectype
      );
    } else {
      return await setActiveTimeframeAPI(
        asset.symbol,
        asset.broker,
        timeframe,
        'tos',
        asset.broker === 'okx' ? username : asset.account,
        asset.sectype
      );
    }
  };

  const sanitizeQuantityText = (text) => {
    if (text == null) return '';
    let t = String(text).replace(/,/g, '.');
    t = t.replace(/[^0-9.]/g, '');
    const firstDotIndex = t.indexOf('.');
    if (firstDotIndex !== -1) {
      const before = t.slice(0, firstDotIndex + 1);
      const after = t.slice(firstDotIndex + 1).replace(/\./g, '');
      t = before + after;
    }
    return t;
  };

  const handleQuantityChange = (level, inputValue) => {
    const normalizedText = sanitizeQuantityText(inputValue || '');
    setQuantitiesInput(prev => ({ ...prev, [`${level}_quantity`]: normalizedText }));
  };

  const handleQuantityBlur = (level) => {
    const rawValue = quantitiesInput[`${level}_quantity`];
    if (rawValue === undefined) return;

    const incomplete = rawValue === '.';
    const parsed = incomplete || String(rawValue).trim() === '' ? null : parseFloat(rawValue);
    const finalValue = parsed === 0 ? null : parsed;
    setQuantitiesInput(prev => ({ ...prev, [`${level}_quantity`]: finalValue }));
    onQuantitySet(
      level,
      finalValue,
      asset.symbol,
      asset.broker,
      asset.sectype,
      timeframe,
      asset.account
    );
  };

  const connectWebsocket = () => {
    ws.current = new WebSocket(`${process.env.REACT_APP_API_WS_BASE_URL}/fetch-sources-tos-socket`);
    const payload = {};
    payload.symbol = asset.symbol;
    payload.sectype = asset.sectype;
    payload.broker = asset.broker;
    payload.account = asset.broker === 'ib' ? asset.account : username;
    payload.strategy = strategy;
    payload.timeframe = timeframe;
    payload.Authentication = getAuthToken();
    ws.current.onopen = () => { ws.current.send(JSON.stringify(payload)); setwsConnected(true); console.log(`connection estabilished for ${asset.symbol}`); };
    ws.current.onclose = () => { setwsConnected(false); console.log(`connection released for ${asset.symbol}`) }
    ws.current.onerror = () => { setwsConnected(false); };
    gettingSourcesData();
  };

  const gettingSourcesData = useCallback(() => {
    if (!ws.current) return;

    ws.current.onmessage = body => {
      const data = body.data;
      if (data !== 'ping') {
        const message = JSON.parse(body.data);
        var sourcesWs = message.sources;
        const currentTfKey = timeframe;
        const tosSources = sourcesWs.filter(item => item.strategy === 'tos').sort((a, b) => a.sensitivity - b.sensitivity);
        setSources(tosSources);
        onSourcesChange(tosSources, currentTfKey);
        message.statuses.tos_patch = JSON.parse(message.statuses.tos_patch)
        message.statuses.counter_values = JSON.parse(message.statuses.counter_values)
        setStatuses(message.statuses);
        setQuantitiesInput(message.statuses.tos_patch);
        setCountersData(message.statuses.counter_values);
        setTimeframeStates(
          message.statuses.timeframe_states
            ? JSON.parse(message.statuses.timeframe_states)
            : {}
        );
        onPatchChange(message.statuses.tos_patch, currentTfKey);

        if (!isInitialDataLoaded.current && message.statuses.tos_patch) {
          const p = message.statuses.tos_patch;
          setShowUPanel(!!(p.u1_quantity || p.u2_quantity || p.u3_quantity));
          setShowLPanel(!!(p.l1_quantity || p.l2_quantity || p.l3_quantity));
          isInitialDataLoaded.current = true;
        }
        if (message.statuses.leverage) {
          setLeverage(message.statuses.leverage)
        } else {
          setLeverage(leverageInitial)
        }

        if (message.statuses.sts_object) {
          try {
            const parsedStsData = JSON.parse(message.statuses.sts_object);
            setStsData(parsedStsData);
            setStsActive(parsedStsData.updown_target || parsedStsData.fade_target || parsedStsData.fade_shabbat || parsedStsData.trading_spagat || parsedStsData.jump_to || parsedStsData.shabbat)
            onStsDataChange(parsedStsData);
          } catch (error) {
            console.error('[TOSPanel] Error parsing STS data:', error);
            setStsData(null);
            setStsActive(false);
            onStsDataChange(null);
          }
        } else {
          setStsData(null);
          setStsActive(false);
          onStsDataChange(null);
        }

        const duSocketMuted = duSavesInFlight.current > 0
          || Date.now() < duSocketMuteUntil.current;
        if (message.statuses.django_unchained_tos_config && !duSocketMuted) {
          try {
            const parsedConfig = JSON.parse(message.statuses.django_unchained_tos_config);
            const nextDuConfigs = {
              tactical: extractLevelConfig(parsedConfig.tactical),
              optimal: extractLevelConfig(parsedConfig.optimal),
              strategic: extractLevelConfig(parsedConfig.strategic || parsedConfig.senior || parsedConfig),
            };
            setDuLevelConfigs(nextDuConfigs);
            setShowDuSelectors(createInitialDuVisibility);
          } catch (error) {
            console.error('[TOSPanel] Error parsing Django Unchained config:', error);
          }
        }

        if (message.statuses.active_wave) {
          setActiveWave(message.statuses.active_wave);
        }

        if (message.statuses.influence_arrows) {
          try {
            setInfluenceArrows(JSON.parse(message.statuses.influence_arrows));
          } catch (error) {
            console.error('[TOSPanel] Error parsing influence arrows:', error);
          }
        }

        if (message.statuses.trend_extension) {
          try {
            setTrendExtensionState(JSON.parse(message.statuses.trend_extension));
          } catch (error) {
            console.error('[TOSPanel] Error parsing trend extension:', error);
          }
        }

        if (message.statuses.x4_ladder) {
          try {
            setX4Ladder(JSON.parse(message.statuses.x4_ladder));
          } catch (error) {
            console.error('[TOSPanel] Error parsing x4 ladder:', error);
          }
        }

        // Reordered: U signals above TMS, L signals below TMS, E removed
        const temp_families = ['u1', 'u2', 'u3', 't', 'm', 's', 'l1', 'l2', 'l3']
        const temp_gyrox_array = [];
        sourcesWs = sourcesWs.filter(item => item.strategy === 'tos').sort((a, b) => a.sensitivity - b.sensitivity);
        for (let i = 0; i < temp_families.length && sourcesWs.length > 0; i++) {
          const prefix = temp_families[i];
          const items = sourcesWs.filter(item => item.source_name.startsWith(prefix));
          temp_gyrox_array.push(items);
          sourcesWs = sourcesWs.filter(item => !item.source_name.startsWith(prefix));
        }
        setGyroxArray(temp_gyrox_array);
      }
    };
  }, [onStsDataChange])

  useEffect(() => {
    return () => {
      closeWebsocket();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [])

  if (gridLocation === undefined) {
    gridLocation = "sm:col-start-2 sm:col-end-2 sm:row-start-1 col-start-1 col-end-1 row-start-1 row-end-1";
  }

  // Helper: color for signal card background based on family type
  const getCardBg = (familyPrefix) => {
    const p = familyPrefix.toLowerCase();
    if (p.startsWith('u') || p.startsWith('l')) return 'bg-olive-50 border-olive-200';
    return 'bg-olive-50 border-olive-200';
  };

  // Helper: signal label style
  const getSignalLabelStyle = (familyPrefix) => {
    const p = familyPrefix.toLowerCase();
    if (p.startsWith('u') || p.startsWith('l')) return 'bg-olive-50 border-olive-600 text-olive-500';
    return 'bg-olive-50 border-olive-600 text-olive-500';
  };

  // Helper: sub-source tile style
  const getSubSourceStyle = (sub_source) => {
    try {
      const prefix = sub_source.source_name[0].toLowerCase();
      if (prefix === 'u') return 'bg-olive-200 border-olive-200 shadow-sm shadow-olive-200';
      if (prefix === 'l') return 'bg-olive-200 border-olive-200 shadow-sm shadow-olive-200';

      // TMS sources — use the same red/green as U/L
      if (sub_source.side.toUpperCase() === 'BUY') {
        let style = sub_source.isActive ? 'bg-emerald-500 shadow-sm shadow-emerald-200' : 'bg-emerald-400 shadow-sm shadow-emerald-200';
        if ([statuses.upper_bound, statuses.lower_bound].includes(sub_source.sensitivity)) {
          style += ' ring-2 ring-orange-400 ring-offset-1 ring-offset-white';
        }
        return style;
      } else {
        let style = sub_source.isActive ? 'bg-red-500 shadow-sm shadow-red-200' : 'bg-red-400 shadow-sm shadow-red-200';
        if ([statuses.upper_bound, statuses.lower_bound].includes(sub_source.sensitivity)) {
          style += ' ring-2 ring-cyan-400 ring-offset-1 ring-offset-white';
        }
        return style;
      }
    } catch (e) {
      return 'bg-gray-400';
    }
  };

  // Determine level key for a family
  const getLevelKey = (family) => {
    if (!family || family.length === 0) return null;
    const name = family[0].source_name.toLowerCase();
    if (name.length >= 2 && sourceToLevelMap[name]) return sourceToLevelMap[name];
    if (sourceToLevelMap[name[0]]) return sourceToLevelMap[name[0]];
    return null;
  };

  // Check if family is TMS (has counter dropdown)
  const isTMSFamily = (family) => {
    if (!family || family.length === 0) return false;
    return ['t', 'm', 's'].includes(family[0].source_name[0].toLowerCase());
  };

  const doLocalReconnect = () => {
    setIsReconnecting(true);
    isInitialDataLoaded.current = false;
    closeWebsocket();
    connectWebsocket();
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => setIsReconnecting(false), 900);
  };

  const handleReconnect = () => {
    doLocalReconnect();
    onReconnect(); // also restart the parent-owned timeframes socket
  };

  const currentTf = timeframe;

  const renderDuSelector = (levelKey) => {
    if (!DU_LEVEL_ORDER.includes(levelKey)) return null;
    const levelConfig = duLevelConfigs[levelKey] || createEmptyDuLevelConfig();
    const isVisible = !!showDuSelectors[levelKey];
    const hasSelection = hasSelectedDuWaves(levelConfig);
    const parityMode = levelConfig.parityMode || 'all';
    const isCurrentSenior = currentSeniorDuLevel === levelKey;
    const btnBase = 'flex-1 py-1 rounded text-[10px] font-medium transition-all duration-150 border';

    return (
      <div className="mt-2 rounded-lg border border-gray-200 bg-white/80 p-2">
        <button
          type="button"
          onClick={() => setShowDuSelectors((prev) => enforceExpandedDuSelector(prev, levelKey))}
          className="flex w-full items-center justify-between text-left"
        >
          <div className="flex items-center gap-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              DU Waves
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasSelection && !isVisible && (
              <span className="text-[10px] font-semibold text-purple-500">
                selected
              </span>
            )}
            <svg
              className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${isVisible ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        {isVisible && (
          <div className="mt-2 flex flex-col gap-1.5">
            <div className="flex gap-1">
              {DU_PARITY_OPTIONS.map((option) => {
                const isActiveMode = parityMode === option.value;
                return (
                  <button
                    key={`${levelKey}-parity-${option.value}`}
                    onClick={() => handleDuParityModeChange(levelKey, option.value)}
                    className={`flex-1 py-1 rounded text-[10px] font-semibold border transition-all duration-200 ${
                      isActiveMode
                        ? 'bg-slate-700 text-white border-slate-600 shadow-sm'
                        : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
              {levelKey !== 'strategic' && (
                <button
                  type="button"
                  onClick={() => handleDuCloseModeToggle(levelKey)}
                  title="What this non-senior level does on its nearest selected wave. OPEN: trade the level quantity along the wave it lands on (flips to CLOSE once filled). CLOSE: send the level quantity back against the entry side and zero the level."
                  className={`flex-1 py-1 rounded text-[10px] font-semibold border transition-all duration-200 ${
                    levelConfig.closeMode
                      ? 'bg-amber-500 text-white border-amber-400 shadow-sm'
                      : 'bg-emerald-600 text-white border-emerald-500 shadow-sm'
                  }`}
                >
                  {levelConfig.closeMode ? 'CLOSE' : 'OPEN'}
                </button>
              )}
            </div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => {
                const key = `u${n}`;
                const isActive = countersData[`counter_state_${levelKey}`] === `U${n}` || activeWave === `U${n}`;
                const isDisabled = !waveMatchesParityMode(key, parityMode);
                return (
                  <button
                    key={`${levelKey}-u-${n}`}
                    disabled={isDisabled}
                    onClick={() => handleDuWavePairToggle(levelKey, key, 'erasable')}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleDuWavePairToggle(levelKey, key, 'persistent');
                    }}
                    className={`${btnBase} ${
                      getDuWaveButtonStateClass({
                        isDisabled,
                        isPersistent: levelConfig.persistentWavePairs[key],
                        isSelected: levelConfig.wavePairs[key],
                      })
                    }`}
                    title={`Wave U${n}${isActive ? ' (CURRENT)' : ''} (left click: auto-remove, right click: persistent)`}
                  >
                    U{n}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => {
                const key = `d${n}`;
                const isActive = countersData[`counter_state_${levelKey}`] === `D${n}` || activeWave === `D${n}`;
                const isDisabled = !waveMatchesParityMode(key, parityMode);
                return (
                  <button
                    key={`${levelKey}-d-${n}`}
                    disabled={isDisabled}
                    onClick={() => handleDuWavePairToggle(levelKey, key, 'erasable')}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleDuWavePairToggle(levelKey, key, 'persistent');
                    }}
                    className={`${btnBase} ${
                      getDuWaveButtonStateClass({
                        isDisabled,
                        isPersistent: levelConfig.persistentWavePairs[key],
                        isSelected: levelConfig.wavePairs[key],
                      })
                    }`}
                    title={`Wave D${n}${isActive ? ' (CURRENT)' : ''} (left click: auto-remove, right click: persistent)`}
                  >
                    D{n}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSignalCard = (family) => {
    if (!family || family.length === 0) return null;
    const familyName = family[0].source_name;
    const levelKey = getLevelKey(family);
    const hasTMSCounter = isTMSFamily(family);

    return (
      <div
        key={familyName}
        className={`flex flex-col flex-1 rounded-xl p-2 border transition-all duration-200 ${getCardBg(familyName)}`}
      >
        <div className="mb-1.5 flex flex-shrink-0 items-center justify-center gap-1.5">
          {/* Which real timeframe this level trades. Unbound is called out in
              red: the backend refuses every signal until the map is complete. */}
          {hasTMSCounter && (
            <span
              className={`text-[10px] font-mono ${
                quantitiesInput[`${levelKey}_timeframe`]
                  ? 'text-indigo-600'
                  : 'font-bold text-red-500'
              }`}
              title={
                quantitiesInput[`${levelKey}_timeframe`]
                  ? `Bound to ${quantitiesInput[`${levelKey}_timeframe`]}`
                  : 'Not bound to a timeframe — signals are refused'
              }
            >
              {quantitiesInput[`${levelKey}_timeframe`] || 'N/A'}
            </span>
          )}
          <span className={`inline-block px-2.5 py-0.5 text-xs font-bold rounded-md ${getSignalLabelStyle(familyName)}`}>
            {familyName.toUpperCase()}
          </span>
          {/* Per-level conjugacy override editor is deferred under the matrix
              model — conjugacy is now shown per tile (vs the senior's active). */}
        </div>
        {/* TMS levels: the tile row (wave + conjugacy per selected timeframe)
            replaces the abolished side buttons. Action signals (L/U/e) keep
            their indicators. */}
        <div className="mb-1.5 flex-1 content-start">
          {hasTMSCounter ? renderLevelTiles(levelKey) : (
            <div className="flex flex-wrap justify-center gap-1">
              {family.map((sub_source) => (
                <div
                  key={sub_source.source_name}
                  className={`flex-1 min-w-8 h-7 rounded-lg ${getSubSourceStyle(sub_source)}`}
                  title={sub_source.source_name}
                />
              ))}
            </div>
          )}
        </div>
        {levelKey && (showQty || showDuWaves) && (
          <div className="mt-auto pt-1 flex-shrink-0">
            {showQty && (
              <input
                type="text"
                inputMode="decimal"
                placeholder="Qty"
                value={
                  quantitiesInput[`${levelKey}_quantity`]
                    ? quantitiesInput[`${levelKey}_quantity`]
                    : ''
                }
                onChange={(e) => handleQuantityChange(levelKey, e.target.value)}
                onBlur={() => handleQuantityBlur(levelKey)}
                className="w-full min-w-0 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-700 placeholder-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-300 focus:border-purple-300 transition-colors"
              />
            )}
            {showDuWaves && renderDuSelector(levelKey)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${gridLocation}`}>
      <div
        className={`bg-white ${noBottomMargin ? '' : 'mb-4'} mr-4 rounded-2xl overflow-visible border border-gray-200`}
        style={{
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* ── Header ── */}
        <div className="px-4 pt-3 pb-2">
          {/* Account name (centered, not for OKX) + TRADING/SCANNER switch */}
          {((showAccount && asset.broker !== 'okx' && asset.account) || onToggleTradingInstrument || onCycleScannerWaveFilter) && (
            <div className="mb-3 flex items-center justify-center gap-2">
              {showAccount && asset.broker !== 'okx' && asset.account && (
                <span className="max-w-[60%] truncate text-sm font-semibold text-gray-500">
                  {asset.account}
                </span>
              )}
              {instrumentTypeBadge(asset.sectype) && (
                <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                  {instrumentTypeBadge(asset.sectype)}
                </span>
              )}
              {onToggleTradingInstrument && (
                <button
                  type="button"
                  onClick={onToggleTradingInstrument}
                  title="Toggle trading / scanner for this instrument"
                  className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold tracking-wide transition-all ${tradingInstrumentActive
                    ? 'bg-olive-600 text-white hover:bg-olive-700'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  {tradingInstrumentActive ? 'TRADING' : 'SCANNER'}
                </button>
              )}
              {/* Wave filter of the current mode: SCANNER -> scanner channel, TRADING -> main channel. */}
              {onCycleScannerWaveFilter && (
                <button
                  type="button"
                  onClick={onCycleScannerWaveFilter}
                  title={tradingInstrumentActive ? 'Waves announced on the main channel' : 'Waves the scanner notifies on'}
                  className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-[11px] font-bold tracking-wide text-gray-500 transition-all hover:bg-gray-200"
                >
                  {SCANNER_WAVE_FILTER_LABELS[scannerWaveFilter] || SCANNER_WAVE_FILTER_LABELS.first}
                </button>
              )}
            </div>
          )}
          <div className="relative flex items-start justify-between gap-2 min-w-0">
            {/* Status badges */}
            {statuses && (
              <div className="flex flex-wrap gap-1 shrink-0">
                {Object.entries(statuses)
                  .map(([key, value]) => [key, statusBadgeLabel(key, value)])
                  .filter((entry) => entry[1] !== null)
                  .map((entry) => (
                    <span
                      key={entry[0]}
                      className="px-1.5 py-0.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-500 border border-gray-200"
                    >
                      {entry[1]}
                    </span>
                  ))}
              </div>
            )}

            {/* Symbol & Timeframe */}
            <div className="flex flex-1 items-center justify-start gap-2 min-w-0 pl-1 sm:pl-2">
              <div className="min-w-0 shrink">
                <span className="text-lg font-bold text-gray-800 tracking-wider">
                  {asset.broker === 'okx'
                    ? asset.symbol.substring(0, asset.symbol.indexOf('/'))
                    : asset.symbol}
                </span>
                {isSenior ? (
                  <select
                    value={timeframe}
                    onChange={(e) => onTimeframeChange(e.target.value)}
                    className="ml-2 bg-gray-50 rounded-lg border border-gray-300 text-gray-700 text-sm px-2 py-0.5"
                  >
                    {timeframesTos.map(tf => (
                      <option key={tf} value={tf}>{tf}</option>
                    ))}
                  </select>
                ) : (
                  <span className="ml-2 text-sm font-medium text-gray-400">
                    {currentTf.toUpperCase()}
                  </span>
                )}
              </div>
              {asset.broker === 'okx' && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[11px] text-gray-400">Lev</span>
                  <input
                    type="text"
                    value={leverage}
                    onChange={(e) => handleLeverageChange(sanitizeQuantityText(e.target.value))}
                    className="w-10 sm:w-11 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 text-xs px-1.5 py-0.5 focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 shrink min-w-0">
              <WebSocketStatusBadge connected={wsConnected} onReconnect={handleReconnect} isReconnecting={isReconnecting} className="shrink min-w-0" />
            </div>
          </div>

          {/* ── Timeframe Toggle (A / B / C) ── */}
          <div className="flex justify-center mt-3 gap-1 bg-gray-100 rounded-xl p-1">
            {['A', 'B', 'C'].map((tf) => (
              <button
                key={tf}
                onClick={() => handleSetActiveTimeframeAPI(tf)}
                className={`flex-1 px-4 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200 ${currentTf === tf
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>

        {rangeFinderEnabled && currentTf !== 'ZERO' && (
          <RangeFinderPanel asset={asset} username={username} />
        )}

        {/* ── Signal Grid (column-oriented: U | TMS | L) ── */}
        {currentTf !== 'ZERO' && gyroxArray.length >= 9 && (
          <div className="px-3 pb-3 overflow-visible">
            <div className="flex gap-1.5 transition-all duration-300 items-stretch h-full w-full overflow-visible">
              {/* U Column */}
              {showSideSignals && (
              <div className={`flex flex-col gap-1.5 transition-all duration-300 min-w-0 overflow-visible ${showUPanel ? 'flex-[1_1_0%]' : 'w-8 shrink-0'}`}>
                <div className={`flex items-center ${showUPanel ? 'justify-between' : 'justify-center'} mb-1 px-1 h-4 min-w-0`}>
                  {showUPanel && <div className="text-xs font-semibold text-olive-600 tracking-widest uppercase truncate whitespace-nowrap min-w-0">U</div>}
                  <button onClick={() => setShowUPanel(!showUPanel)} className="text-olive-400 hover:text-olive-600 transition-colors flex-shrink-0" title={showUPanel ? "Hide U Panel" : "Show U Panel"}>
                    <svg className={`w-4 h-4 transform transition-transform duration-300 ${showUPanel ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showUPanel ? "M15 19l-7-7 7-7" : "M4 6h16M4 12h16M4 18h16"} />
                    </svg>
                  </button>
                </div>
                {showUPanel ? (
                  <>
                    {[0, 1, 2].map(idx => renderSignalCard(gyroxArray[idx]))}
                  </>
                ) : (
                  <div
                    onClick={() => setShowUPanel(true)}
                    className="flex-1 min-h-[120px] rounded-xl border border-olive-200 bg-olive-50/50 hover:bg-olive-100 flex items-center justify-center cursor-pointer transition-colors group"
                  >
                    <div className="rotate-180 [writing-mode:vertical-rl] text-[10px] font-bold text-olive-400 tracking-widest group-hover:text-olive-600 whitespace-nowrap">U SIGNALS</div>
                  </div>
                )}
              </div>
              )}

              {/* TMS Column */}
              <div className="flex flex-col gap-1.5 flex-[1_1_0%] min-w-0 shrink-0 overflow-visible">
                <div className="flex items-center justify-center mb-1 px-1 h-4 min-w-0">
                  <div className="text-xs font-semibold text-olive-600 tracking-widest uppercase truncate whitespace-nowrap min-w-0">TMS</div>
                </div>
                {[3, 4, 5].map(idx => renderSignalCard(gyroxArray[idx]))}
              </div>

              {/* L Column */}
              {showSideSignals && (
              <div className={`flex flex-col gap-1.5 transition-all duration-300 min-w-0 overflow-visible ${showLPanel ? 'flex-[1_1_0%]' : 'w-8 shrink-0'}`}>
                <div className={`flex flex-row-reverse items-center ${showLPanel ? 'justify-between' : 'justify-center'} mb-1 px-1 h-4 min-w-0`}>
                  {showLPanel && <div className="text-xs font-semibold text-olive-600 tracking-widest uppercase truncate whitespace-nowrap min-w-0">L</div>}
                  <button onClick={() => setShowLPanel(!showLPanel)} className="text-olive-400 hover:text-olive-600 transition-colors flex-shrink-0" title={showLPanel ? "Hide L Panel" : "Show L Panel"}>
                    <svg className={`w-4 h-4 transform transition-transform duration-300 ${showLPanel ? '' : 'rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showLPanel ? "M9 5l7 7-7 7" : "M4 6h16M4 12h16M4 18h16"} />
                    </svg>
                  </button>
                </div>
                {showLPanel ? (
                  <>
                    {[6, 7, 8].map(idx => renderSignalCard(gyroxArray[idx]))}
                  </>
                ) : (
                  <div
                    onClick={() => setShowLPanel(true)}
                    className="flex-1 min-h-[120px] rounded-xl border border-olive-200 bg-olive-50/50 hover:bg-olive-100 flex items-center justify-center cursor-pointer transition-colors group"
                  >
                    <div className="rotate-180 [writing-mode:vertical-rl] text-[10px] font-bold text-olive-400 tracking-widest group-hover:text-olive-600 whitespace-nowrap">L SIGNALS</div>
                  </div>
                )}
              </div>
              )}
            </div>
            <TimeframeMatrix
              timeframeStates={timeframeStates}
              binding={quantitiesInput}
              asset={asset}
              username={username}
              onSetDisplayList={handleSetDisplayList}
              defaultCollapsed={timeframesCollapsed}
            />
          </div>
        )}

        {/* ── Mobile: Set Active Menu ── */}
        {window.screen.width <= 450 && (
          <div className="relative inline-block text-left mt-2 px-3 pb-3">
            <div onClick={() => setIsActiveMenuOpen(!isActiveMenuOpen)}>
              <button
                type="button"
                className="inline-flex w-full justify-center gap-x-1.5 rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm border border-gray-200 hover:bg-gray-200"
              >
                Set active
                <svg className="-mr-1 size-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            {isActiveMenuOpen && (
              <div className="absolute left-0 z-10 mt-1 w-56 origin-top-left divide-y divide-gray-100 rounded-lg bg-white shadow-xl border border-gray-200">
                {gyroxArray.map(family => (
                  <div className="py-1">
                    {family.map(sub_source => (
                      <div
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        onClick={() => { setIsActiveMenuOpen(!isActiveMenuOpen); handleChangeActivePos(sub_source.source_name, asset, currentTf) }}
                      >
                        {sub_source.source_name}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

  );
};

export default TOSPanel;
